import { useState, useEffect } from 'react';

/**
 * Custom hook for responsive design utilities
 * Provides breakpoint detection and responsive helpers
 */
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  const [breakpoint, setBreakpoint] = useState('lg');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setScreenSize({ width, height });
      
      // Determine breakpoint
      if (width < 640) {
        setBreakpoint('sm');
      } else if (width < 768) {
        setBreakpoint('md');
      } else if (width < 1024) {
        setBreakpoint('lg');
      } else if (width < 1280) {
        setBreakpoint('xl');
      } else {
        setBreakpoint('2xl');
      }
    };

    // Set initial size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Breakpoint checks
  // isMobile: phones and small tablets (< 768px)
  const isMobile = breakpoint === 'sm' || breakpoint === 'md';
  // isTablet: tablets only (768–1024px)
  const isTablet = breakpoint === 'lg';
  const isDesktop = breakpoint === 'xl' || breakpoint === '2xl';
  // isSmallScreen: everything below desktop (< 1024px) — use for sidebar/drawer logic
  const isSmallScreen = breakpoint === 'sm' || breakpoint === 'md' || breakpoint === 'lg';
  const isLargeScreen = breakpoint === 'xl' || breakpoint === '2xl';

  // Responsive values
  const getResponsiveValue = (values) => {
    if (isMobile) return values.mobile || values.sm || values.default;
    if (isTablet) return values.tablet || values.md || values.lg || values.default;
    if (isDesktop) return values.desktop || values.xl || values['2xl'] || values.default;
    return values.default;
  };

  // Grid columns based on screen size
  const getGridCols = (base = 1) => {
    if (isMobile) return base;
    if (isTablet) return Math.min(base * 2, 4);
    if (isDesktop) return Math.min(base * 3, 6);
    return base;
  };

  // Spacing based on screen size
  const getSpacing = (base = 4) => {
    if (isMobile) return base;
    if (isTablet) return base + 2;
    if (isDesktop) return base + 4;
    return base;
  };

  // Font size based on screen size
  const getFontSize = (base = 'base') => {
    if (isMobile) return 'sm';
    if (isTablet) return base;
    if (isDesktop) return 'lg';
    return base;
  };

  // Padding based on screen size
  const getPadding = (base = 4) => {
    if (isMobile) return base;
    if (isTablet) return base + 2;
    if (isDesktop) return base + 4;
    return base;
  };

  // Chart dimensions based on screen size
  const getChartDimensions = () => {
    if (isMobile) return { width: '100%', height: 200 };
    if (isTablet) return { width: '100%', height: 300 };
    if (isDesktop) return { width: '100%', height: 400 };
    return { width: '100%', height: 300 };
  };

  // Sidebar behavior
  const getSidebarBehavior = () => {
    if (isMobile) return { collapsed: true, overlay: true };
    if (isTablet) return { collapsed: false, overlay: false };
    if (isDesktop) return { collapsed: false, overlay: false };
    return { collapsed: false, overlay: false };
  };

  // Navigation behavior
  const getNavigationBehavior = () => {
    if (isMobile) return { type: 'mobile', position: 'bottom' };
    if (isTablet) return { type: 'sidebar', position: 'left' };
    if (isDesktop) return { type: 'sidebar', position: 'left' };
    return { type: 'sidebar', position: 'left' };
  };

  return {
    screenSize,
    breakpoint,
    isMobile,
    isTablet,
    isDesktop,
    isSmallScreen,
    isLargeScreen,
    getResponsiveValue,
    getGridCols,
    getSpacing,
    getFontSize,
    getPadding,
    getChartDimensions,
    getSidebarBehavior,
    getNavigationBehavior,
  };
};

/**
 * Hook for mobile-specific functionality
 */
export const useMobile = () => {
  const { isMobile, isSmallScreen } = useResponsive();
  
  return {
    isMobile,
    isSmallScreen,
    // Mobile-specific utilities
    shouldCollapseSidebar: isMobile,
    shouldShowMobileNav: isMobile,
    shouldUseTouchTargets: isMobile,
    shouldOptimizeForTouch: isMobile,
  };
};

/**
 * Hook for responsive grid layouts
 */
export const useResponsiveGrid = (config = {}) => {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  
  const {
    mobile = 1,
    tablet = 2,
    desktop = 3,
    gap = 4,
    padding = 4,
  } = config;

  const getGridConfig = () => {
    if (isMobile) return { cols: mobile, gap, padding };
    if (isTablet) return { cols: tablet, gap: gap + 2, padding: padding + 2 };
    if (isDesktop) return { cols: desktop, gap: gap + 4, padding: padding + 4 };
    return { cols: desktop, gap, padding };
  };

  const getGridClasses = () => {
    const { cols, gap, padding } = getGridConfig();
    return `grid grid-cols-${mobile} sm:grid-cols-${tablet} lg:grid-cols-${desktop} gap-${gap} p-${padding}`;
  };

  return {
    getGridConfig,
    getGridClasses,
    isMobile,
    isTablet,
    isDesktop,
  };
};

/**
 * Hook for responsive charts
 */
export const useResponsiveChart = (config = {}) => {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  
  const {
    mobileHeight = 200,
    tabletHeight = 300,
    desktopHeight = 400,
    mobileWidth = '100%',
    tabletWidth = '100%',
    desktopWidth = '100%',
  } = config;

  const getChartConfig = () => {
    if (isMobile) return { width: mobileWidth, height: mobileHeight };
    if (isTablet) return { width: tabletWidth, height: tabletHeight };
    if (isDesktop) return { width: desktopWidth, height: desktopHeight };
    return { width: '100%', height: 300 };
  };

  const getChartClasses = () => {
    return 'w-full h-48 sm:h-64 lg:h-80 xl:h-96';
  };

  return {
    getChartConfig,
    getChartClasses,
    isMobile,
    isTablet,
    isDesktop,
  };
};

export default useResponsive;












